import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { FiSearch, FiUpload, FiLoader, FiInfo, FiZap, FiMapPin, FiBriefcase, FiAlertTriangle, FiCpu, FiHelpCircle, FiX } from 'react-icons/fi';
import useApi from '../hooks/useApi';
import { useUser } from '../context/UserContext';
import { AuthContext } from '../context/AuthContext';
import { searchCandidatesWithAI } from '../services/aiService';
import { checkCreditAvailability, deductCredits } from '../services/creditService';
import PerformanceTrackingService from '../services/PerformanceTrackingService';
import GradientButton from '../components/ui/GradientButton';
import AIInfoTooltip from '../components/ui/AIInfoTooltip';
import AIDocumentation from '../components/ai/AIDocumentation';
import { motion, AnimatePresence } from 'framer-motion';
import { FiDownload } from 'react-icons/fi';

const DatabasePage = () => {
  const navigate = useNavigate();
  const { data: responseData, loading, error } = useApi('/candidates');
  const { user: authUser } = useContext(AuthContext);
  const isRecruiter = authUser?.userType === 'recruiter';
  const { user } = useUser();
  
  // Fetch dashboard data to get AI credits
  const { data: dashboardData, refetch: refetchDashboard } = useApi('/dashboard');
  const aiCredits = dashboardData?.data?.aiCredits?.available || 0;
  
  const [activeTab, setActiveTab] = useState('regular'); // 'regular' or 'ai'
  
  // Regular search state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [minExperience, setMinExperience] = useState('');
  const [maxExperience, setMaxExperience] = useState('');
  const [location, setLocation] = useState('');
  const [education, setEducation] = useState([]);
  const [activeIn, setActiveIn] = useState('6 months');
  const [workMode, setWorkMode] = useState('');
  const [category, setCategory] = useState('');
  
  // AI search state
  const [selectedJobId, setSelectedJobId] = useState('');
  const [aiSearchResults, setAiSearchResults] = useState([]);
  const [isAiSearching, setIsAiSearching] = useState(false);
  const [aiSearchIntent, setAiSearchIntent] = useState('');
  const [aiSearchError, setAiSearchError] = useState(null);
  const [searchMode, setSearchMode] = useState('job'); // 'job' or 'custom'
  const [maxProfilesToSearch, setMaxProfilesToSearch] = useState(10);
  
  // Credit management state
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [showCreditWarning, setShowCreditWarning] = useState(false);
  const [requiredCredits, setRequiredCredits] = useState(0);
  const [, setCreditDeficit] = useState(0);
  const [lastSearchTimestamp, setLastSearchTimestamp] = useState(null);
  const [showAIDocumentation, setShowAIDocumentation] = useState(false);
  
  // Jobs data
  const { data: jobsData, loading: jobsLoading, error: jobsError } = useApi('/jobs');
  const jobs = useMemo(() => {
    // API returns { success: true, data: { jobs: [...], pagination: {...} } }
    const extractedJobs = jobsData?.data?.jobs || jobsData?.jobs || [];
    console.log('Jobs loaded:', extractedJobs.length, 'jobs');
    if (extractedJobs.length > 0) {
      console.log('First job:', extractedJobs[0].title);
    }
    return extractedJobs;
  }, [jobsData]);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Effect to update selected job when job ID changes
  useEffect(() => {
    if (selectedJobId) {
      const job = jobs.find(job => job.id.toString() === selectedJobId.toString());
      setSelectedJob(job || null);
    } else {
      setSelectedJob(null);
    }
  }, [selectedJobId, jobs]);
  
  // Extract candidates array from the response data with robust error handling
  const candidates = useMemo(() => {
    try {
      if (!responseData) return [];
      
      // Handle different response formats
      if (Array.isArray(responseData)) {
        return responseData; // Direct array
      } else if (responseData.data && Array.isArray(responseData.data)) {
        return responseData.data; // Standard API response format
      } else if (responseData.success && responseData.data && responseData.data.data && Array.isArray(responseData.data.data)) {
        return responseData.data.data; // Nested success wrapper format from PHP API
      } else if (responseData.candidates && Array.isArray(responseData.candidates)) {
        return responseData.candidates; // Named field format
      } else if (responseData.success && responseData.data) {
        // Try to handle the nested structure from our PHP API
        const { data } = responseData;
        if (data.data && Array.isArray(data.data)) {
          return data.data; // Nested data structure
        }
      }
      
      console.warn('Unexpected candidates data format:', responseData);
      return [];
    } catch (error) {
      console.error('Error extracting candidates data:', error);
      return [];
    }
  }, [responseData]);

  const filteredCandidates = useMemo(() => {
    if (!candidates || !Array.isArray(candidates)) return [];
    
    return candidates.filter(candidate => {
      // Safety check for candidate object
      if (!candidate || typeof candidate !== 'object') return false;
      
      try {
        // Safe property access with fallbacks
        const name = candidate.name || '';
        const role = candidate.role || '';
        const status = candidate.status || '';
        const skills = candidate.skills || [];
        const candidateExperience = candidate.experience || 0;
        const candidateLocation = candidate.location || '';
        
        // Safe lowercase conversion
        const searchTermLower = (searchTerm || '').toLowerCase();
        const nameLower = name.toLowerCase();
        const roleLower = role.toLowerCase();
        const locationLower = candidateLocation.toLowerCase();
        const locationFilterLower = location.toLowerCase();
        
        // Check if name or role matches search term
        const nameMatches = nameLower.includes(searchTermLower);
        const roleMatches = roleLower.includes(searchTermLower);
        
        // Check if any skill matches search term
        let skillMatches = false;
        if (Array.isArray(skills)) {
          skillMatches = skills.some(skill => {
            if (typeof skill === 'string') {
              return skill.toLowerCase().includes(searchTermLower);
            }
            return false;
          });
        }
        
        // Experience range filter
        let experienceRangeMatches = true;
        if (minExperience !== '' && !isNaN(minExperience)) {
          experienceRangeMatches = experienceRangeMatches && candidateExperience >= Number(minExperience);
        }
        if (maxExperience !== '' && !isNaN(maxExperience)) {
          experienceRangeMatches = experienceRangeMatches && candidateExperience <= Number(maxExperience);
        }
        
        // Location filter
        const locationMatches = !location || locationLower.includes(locationFilterLower);
        
        // Education filter
        let educationMatches = true;
        if (education.length > 0) {
          // This is a simplified check - in a real app you'd have more structured education data
          educationMatches = education.some(edu => 
            candidate.education && candidate.education.toLowerCase().includes(edu.toLowerCase())
          );
        }
        
        // Work Mode filter
        const workModeMatches = !workMode || (candidate.workMode && candidate.workMode.toLowerCase() === workMode.toLowerCase());
        
        // Category filter
        const categoryMatches = !category || (candidate.category && candidate.category.toLowerCase().includes(category.toLowerCase()));
        
        const matchesSearch = nameMatches || roleMatches || skillMatches;
        const matchesStatus = statusFilter === 'All' || status === statusFilter;
        
        return matchesSearch && 
               matchesStatus && 
               experienceRangeMatches && 
               locationMatches &&
               educationMatches &&
               workModeMatches &&
               categoryMatches;
      } catch (error) {
        console.error('Error filtering candidate:', error, candidate);
        return false;
      }
    });
  }, [
    searchTerm, 
    statusFilter, 
    candidates, 
    minExperience, 
    maxExperience, 
    location,
    education,
    activeIn,
    workMode,
    category
  ]);

  const uniqueStatuses = useMemo(() => {
    try {
      if (!candidates || !Array.isArray(candidates)) return ['All'];
      
      // Safely extract statuses with error handling
      const statuses = candidates
        .map(c => {
          if (c && typeof c === 'object') {
            return c.status || null;
          }
          return null;
        })
        .filter(Boolean); // Remove null/undefined values
      
      // Use Set to get unique values and convert back to array
      return ['All', ...new Set(statuses)];
    } catch (error) {
      console.error('Error calculating unique statuses:', error);
      return ['All'];
    }
  }, [candidates]);

  const toggleEducation = (edu) => {
    if (education.includes(edu)) {
      setEducation(education.filter(e => e !== edu));
    } else {
      setEducation([...education, edu]);
    }
  };

  // Check if user has enough credits for AI search
  const checkCredits = async (requiredAmount) => {
    if (!user || !user.id) {
      setAiSearchError('User not authenticated');
      return false;
    }
    
    try {
      // Check if user has enough credits (pass user object for recruiter support)
      const creditCheck = await checkCreditAvailability(user.id, requiredAmount, user);
      
      if (!creditCheck.success) {
        setAiSearchError(creditCheck.error || 'Failed to check credit availability');
        return false;
      }
      
      if (!creditCheck.data.hasEnoughCredits) {
        setShowCreditWarning(true);
        setRequiredCredits(requiredAmount);
        setCreditDeficit(creditCheck.data.deficit);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking credits:', error);
      setAiSearchError('Failed to check credit availability');
      return false;
    }
  };
  
  // AI search function that analyzes candidates based on selected job or custom search intent
  const performAiSearch = async () => {
    // Reset error state
    setAiSearchError(null);
    
    // Validate search parameters
    if (searchMode === 'job' && !selectedJob) {
      setAiSearchError('Please select a job to find matching candidates');
      return;
    }
    
    if (searchMode === 'custom' && !aiSearchIntent.trim()) {
      setAiSearchError('Please enter a search description or requirements');
      return;
    }
    
    // Calculate required credits
    const requiredAmount = maxProfilesToSearch;
    setRequiredCredits(requiredAmount);
    
    // Check if user has enough credits
    const hasEnoughCredits = await checkCredits(requiredAmount);
    if (!hasEnoughCredits) return;
    
    // Start search process
    setIsAiSearching(true);
    setShowCreditWarning(false);
    
    try {
      // Prepare search parameters
      const searchParams = {
        jobDescription: searchMode === 'job' 
          ? (() => {
              // Safely build job description
              let desc = selectedJob.title || '';
              if (selectedJob.description) desc += `. ${selectedJob.description}`;
              
              // Handle requirements (might be string or array)
              if (selectedJob.requirements) {
                const reqs = Array.isArray(selectedJob.requirements) 
                  ? selectedJob.requirements.join('. ') 
                  : selectedJob.requirements;
                desc += `. Requirements: ${reqs}`;
              }
              
              // Handle skills (might be string or array)
              if (selectedJob.skills) {
                const skills = Array.isArray(selectedJob.skills)
                  ? selectedJob.skills.join(', ')
                  : selectedJob.skills;
                desc += `. Skills: ${skills}`;
              }
              
              return desc;
            })()
          : aiSearchIntent,
        maxResults: maxProfilesToSearch,
        planType: user?.subscription?.plan || 'basic',
        availableCredits: aiCredits
      };
      
      // Call AI service to search candidates
      const response = await searchCandidatesWithAI(searchParams);

      if (!response.success) {
        setAiSearchError(response.error || 'Failed to perform AI search');
        setIsAiSearching(false);
        return;
      }
      
      // Process results
      const { candidates: matchedCandidates, creditsUsed: usedCredits } = response.data;

      // Credits are already deducted by the backend API
      // No need to deduct again here to avoid double-charging
      if (usedCredits > 0) {
        setCreditsUsed(usedCredits);

        // Refresh dashboard to update credit display
        refetchDashboard();
      }
      
      // Update results and timestamp
      setAiSearchResults(matchedCandidates);
      setLastSearchTimestamp(new Date().toISOString());
      
      // Track database search activity
      if (isRecruiter) {
        try {
          await PerformanceTrackingService.trackDatabaseSearch({
            query: searchMode === 'job' ? selectedJob?.title : aiSearchIntent,
            filters: {
              location: location || '',
              experience: `${minExperience || 0}-${maxExperience || 'any'}`,
              skills: searchMode === 'job' ? selectedJob?.skills?.join(', ') : ''
            },
            resultsCount: matchedCandidates.length
          });
        } catch (error) {
          console.error('Error tracking database search:', error);
        }
        
        // Track AI usage for AI-powered search
        try {
          await PerformanceTrackingService.trackAIUsage({
            feature: 'ai_candidate_search',
            creditsUsed: usedCredits
          });
        } catch (error) {
          console.error('Error tracking AI usage:', error);
        }
      }
    } catch (error) {
      console.error('Error in AI search:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      setAiSearchError(error.message || 'Failed to perform AI search. Please try again.');
    } finally {
      setIsAiSearching(false);
    }
  };
  
  // Function to handle search mode change
  const handleSearchModeChange = (mode) => {
    setSearchMode(mode);
    setAiSearchError(null);
  };

  // Function to track database search
  const trackDatabaseSearch = async () => {
    if (isRecruiter) {
      try {
        // Track the search in the performance metrics
        await PerformanceTrackingService.trackDatabaseSearch({
          query: searchTerm,
          filters: {
            status: statusFilter,
            minExperience,
            maxExperience,
            location,
            education,
            workMode,
            category
          }
        });
      } catch (error) {
        console.error('Error tracking database search:', error);
      }
      setSelectedJob(null);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setMinExperience('');
    setMaxExperience('');
    setLocation('');
    setEducation([]);
    setActiveIn('6 months');
    setWorkMode('');
    setCategory('');
    setSelectedJobId('');
    setAiSearchResults([]);
  };

  return (
    <div className="flex bg-gray-50 min-h-screen font-sans relative">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      <main className="flex-1 p-8 ml-0 md:ml-24">
        <DashboardHeader />
        <div className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-black">Database</h1>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex border-b mb-6">
            <button
              className={`px-6 py-3 font-medium text-sm ${activeTab === 'regular' ? 'border-b-2 border-yellow-400 text-yellow-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => {
                setActiveTab('regular');
                setAiSearchResults([]);
              }}
            >
              <div className="flex items-center gap-2">
                <FiSearch size={16} />
                Regular Search
              </div>
            </button>
            <AIInfoTooltip
              title="AI-Powered Candidate Search"
              content={
                <>
                  <p>Use AI to find the best candidates in your database based on job requirements or custom search criteria.</p>
                  <p className="mt-2">Each search uses 1 AI credit per profile processed.</p>
                </>}
              position="bottom"
              expandable={true}
            >
              <button
                className={`px-6 py-3 font-medium text-sm ${activeTab === 'ai' ? 'border-b-2 border-yellow-400 text-yellow-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('ai')}
              >
                <div className="flex items-center gap-2">
                  <FiZap size={16} />
                  AI-Powered Match
                </div>
              </button>
            </AIInfoTooltip>
          </div>

          {/* Regular Search Tab Content */}
          {activeTab === 'regular' && (
            <div className="bg-white p-6 border border-black mb-6 rounded-lg" style={{ 
              boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-3px) translateY(-3px)'
            }}>
              {/* Experience type selection removed */}

              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Keywords <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  placeholder="Enter keywords such as job title, skills etc."
                  className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Current city/region
                </label>
                <input 
                  type="text" 
                  placeholder="Type to search city/region"
                  className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                <div>
                  <label className="block font-medium mb-2">
                    Experience
                  </label>
                  <div className="flex gap-4">
                    <select 
                      className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
                      value={minExperience}
                      onChange={e => setMinExperience(e.target.value)}
                    >
                      <option value="">Minimum experience</option>
                      <option value="0">0 years</option>
                      <option value="1">1 year</option>
                      <option value="2">2 years</option>
                      <option value="3">3 years</option>
                      <option value="5">5 years</option>
                      <option value="7">7 years</option>
                      <option value="10">10 years</option>
                      <option value="15">15 years</option>
                    </select>
                    <select 
                      className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
                      value={maxExperience}
                      onChange={e => setMaxExperience(e.target.value)}
                    >
                      <option value="">Maximum experience</option>
                      <option value="1">1 year</option>
                      <option value="2">2 years</option>
                      <option value="3">3 years</option>
                      <option value="5">5 years</option>
                      <option value="7">7 years</option>
                      <option value="10">10 years</option>
                      <option value="15">15 years</option>
                      <option value="20">20+ years</option>
                    </select>
                  </div>
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                <div>
                  <label className="block font-medium mb-2">
                    Work Mode
                  </label>
                  <select 
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
                    value={workMode || ''}
                    onChange={e => setWorkMode(e.target.value)}
                  >
                    <option value="">Any work mode</option>
                    <option value="On-site">On-site</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                    <option value="Any">Flexible/Any</option>
                  </select>
                </div>

              </div>

              <div className="mb-4">
                <label className="block font-medium mb-2">
                  Category/Industry
                </label>
                <select 
                  className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
                  value={category || ''}
                  onChange={e => setCategory(e.target.value)}
                >
                  <option value="">Any category</option>
                  <option value="Technology">Technology/IT</option>
                  <option value="Sales">Sales & Marketing</option>
                  <option value="Customer Support">Customer Support</option>
                  <option value="Finance">Finance & Accounting</option>
                  <option value="HR">Human Resources</option>
                  <option value="Operations">Operations</option>
                  <option value="Management">Management</option>
                  <option value="Design">Design & Creative</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Education">Education</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Retail">Retail</option>
                  <option value="Logistics">Logistics</option>
                  <option value="Hospitality">Hospitality</option>
                </select>
              </div>

              <div className="mb-6">
                <label className="block font-medium mb-2">
                  Minimum education
                </label>
                <div className="flex flex-wrap gap-2">
                  {['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate', 'Post Graduate'].map(edu => (
                    <button
                      key={edu}
                      onClick={() => toggleEducation(edu)}
                      className={`px-3 py-1 text-sm border ${
                        education.includes(edu) 
                          ? 'bg-black text-white border-black' 
                          : 'bg-white border-black text-black hover:bg-gray-100'
                      }`}
                    >
                      {edu} {education.includes(edu) && '+'}
                    </button>
                  ))}
                </div>
              </div>


              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Active in</span>
                  <select 
                    className="px-1 py-1 text-sm border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
                    value={activeIn}
                    onChange={e => setActiveIn(e.target.value)}
                  >
                    <option value="6 months">6 months</option>
                    <option value="3 months">3 months</option>
                    <option value="1 month">1 month</option>
                    <option value="2 weeks">2 weeks</option>
                  </select>
                </div>

                <div className="flex gap-3">
                  <button 
                    onClick={resetFilters}
                    className="px-4 py-2 border border-black text-black hover:bg-gray-100"
                  >
                    Reset
                  </button>
                  <button 
                    onClick={trackDatabaseSearch}
                    className="px-4 py-2 bg-black text-white hover:bg-gray-800 flex items-center gap-2"
                  >
                    <FiSearch size={16} />
                    Search candidates
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* AI Documentation Modal */}
          <AnimatePresence>
            {showAIDocumentation && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={() => setShowAIDocumentation(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                      <FiCpu className="text-yellow-500" />
                      AI Features Documentation
                    </h3>
                    <button
                      onClick={() => setShowAIDocumentation(false)}
                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FiX size={20} className="text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                  <div className="p-6 overflow-y-auto">
                    <AIDocumentation feature="search" planType={user?.subscription?.plan || 'basic'} />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* AI Search Tab Content */}
          {activeTab === 'ai' && (
            <div className="bg-white p-6 border border-black mb-6 rounded-lg" style={{ 
              boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-3px) translateY(-3px)'
            }}>
              <div className="flex justify-between items-center mb-4 border-b border-black pb-3">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-black">
                  <FiZap className="text-black" /> AI-Powered Candidate Search
                </h2>
                <button
                  onClick={() => setShowAIDocumentation(true)}
                  className="flex items-center gap-1 text-sm text-black hover:text-gray-700"
                >
                  <FiHelpCircle size={14} />
                  How it works
                </button>
              </div>
              
              {/* Credit Information */}
              <div className="bg-white border border-black p-4 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 border border-black text-black">
                    <FiDollarSign size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">AI Credits Available</p>
                    <p className="text-lg font-bold text-black">
                      {aiCredits} credits
                    </p>
                  </div>
                </div>
                <div className="text-sm text-black">
                  <p>1 credit = 1 profile processed</p>
                  <p className="text-xs mt-1">Using {user?.subscription?.plan || 'basic'} plan model</p>
                </div>
              </div>
              
              {/* Search Mode Tabs */}
              <div className="flex border-b mb-6">
                <button
                  className={`px-4 py-2 font-medium text-sm ${searchMode === 'job' ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => handleSearchModeChange('job')}
                >
                  <div className="flex items-center gap-2">
                    <FiBriefcase size={16} />
                    Search by Job
                  </div>
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm ${searchMode === 'custom' ? 'border-b-2 border-black text-black' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => handleSearchModeChange('custom')}
                >
                  <div className="flex items-center gap-2">
                    <FiSearch size={16} />
                    Custom Search
                  </div>
                </button>
              </div>
              
              {/* Job-based Search */}
              {searchMode === 'job' && (
                <div className="mb-6">
                  <label className="block font-medium mb-2 text-black">Select a job posting</label>
                  <select
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none appearance-none bg-white transition-colors"
                    value={selectedJobId}
                    onChange={(e) => setSelectedJobId(e.target.value)}
                  >
                    <option value="">-- Select a job --</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.title} - {job.location}
                      </option>
                    ))}
                  </select>
                  
                  {selectedJob && (
                    <div className="bg-white p-4 mt-4 border border-black">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-lg text-black">{selectedJob.title}</h3>
                          <p className="text-black">{selectedJob.location}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs border ${selectedJob.status === 'Active' ? 'border-black bg-black text-white' : 'border-black bg-white text-black'}`}>
                          {selectedJob.status}
                        </span>
                      </div>
                      <p className="text-sm text-black mt-3 line-clamp-3">{selectedJob.description}</p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Custom Search */}
              {searchMode === 'custom' && (
                <div className="mb-6">
                  <label className="block font-medium mb-2 text-black">Enter search criteria</label>
                  <textarea
                    className="w-full px-1 py-2 border-b-2 border-gray-300 focus:border-yellow-400 focus:outline-none transition-colors min-h-[120px]"
                    value={aiSearchIntent}
                    onChange={(e) => setAiSearchIntent(e.target.value)}
                    placeholder="Describe the ideal candidate you're looking for. Include skills, experience level, education requirements, etc."
                  />
                  <p className="text-xs text-black mt-2">
                    Example: "Looking for a frontend developer with 3+ years of experience in React, JavaScript, and responsive design. Must have strong UI/UX skills and experience with REST APIs."
                  </p>
                </div>
              )}
              
              {/* Max Profiles Setting */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <label className="block font-medium text-black">Maximum profiles to search</label>
                  <AIInfoTooltip
                    title="AI Credits Usage"
                    content={
                      <>
                        <p>Each profile processed by the AI consumes 1 AI credit.</p>
                        <p className="mt-2">Setting a higher number will search more profiles but use more credits.</p>
                        <p className="mt-2">Pro tip: Start with a smaller number and increase if needed.</p>
                      </>
                    }
                    position="top"
                  >
                    <span className="cursor-help">(?)</span>
                  </AIInfoTooltip>
                </div>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="200"
                    step="1"
                    value={maxProfilesToSearch}
                    onChange={(e) => setMaxProfilesToSearch(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 appearance-none cursor-pointer focus:outline-none"
                  />
                  <span className="w-16 text-center font-medium text-black">{maxProfilesToSearch}</span>
                </div>
                <div className="flex justify-between mt-2">
                  <p className="text-xs text-black flex items-center gap-1">
                    <FiInfo size={12} />
                    Search up to {maxProfilesToSearch} profiles • Pay only for matches (50%+)
                  </p>
                  <p className="text-xs text-black">
                    Available: {aiCredits} credits
                  </p>
                </div>
              </div>
              
              {/* Credit Warning */}
              <AnimatePresence>
                {showCreditWarning && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className="bg-white border border-black p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-1 border border-black text-black mt-0.5">
                          <FiAlertTriangle size={16} />
                        </div>
                        <div>
                          <p className="font-medium text-black">Insufficient AI Credits</p>
                          <p className="text-sm text-black mt-1">
                            You need {requiredCredits} credits for this search, but you only have {aiCredits} available.
                            Please reduce the number of profiles to search or purchase additional credits.
                          </p>
                          <div className="mt-3">
                            <button
                              className="px-4 py-1 bg-black text-white font-medium text-sm"
                              onClick={() => window.location.href = '/pricing'}
                            >
                              Purchase Credits
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              {/* Error Message */}
              <AnimatePresence>
                {aiSearchError && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className="bg-white border border-black p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-1 border border-black text-black mt-0.5">
                          <FiAlertTriangle size={16} />
                        </div>
                        <p className="text-sm text-black">{aiSearchError}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <div className="flex justify-end border-t border-black pt-6 mt-6">
                <button
                  className="px-6 py-2 bg-black text-white font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  onClick={performAiSearch}
                  disabled={isAiSearching || (searchMode === 'job' && !selectedJob) || (searchMode === 'custom' && !aiSearchIntent.trim())}
                >
                  {isAiSearching ? (
                    <>
                      <FiLoader className="animate-spin" size={16} />
                      Processing AI Search...
                    </>
                  ) : (
                    <>
                      <FiZap size={16} />
                      Find Matching Candidates
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && <div className="flex justify-center items-center h-64"><FiLoader className="animate-spin text-black" size={48} /></div>}
          {error && <div className="bg-white border border-black p-4">Error fetching candidates: {error?.message || error}</div>}

          {/* AI Search Results */}
          {activeTab === 'ai' && !loading && !error && (
            <div className="space-y-4">
              {isAiSearching ? (
                <div className="bg-white p-8 border border-black flex flex-col items-center justify-center">
                  <div className="w-16 h-16 border border-black flex items-center justify-center mb-4">
                    <FiLoader size={24} className="text-black animate-spin" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">AI is analyzing candidates</h3>
                  <p className="text-black text-center max-w-md">
                    {searchMode === 'job' 
                      ? `Our AI is matching candidates to the requirements for ${selectedJob?.title}`
                      : 'Our AI is analyzing candidates based on your custom search criteria'}
                  </p>
                  <div className="mt-6 w-full max-w-md bg-gray-100 h-2 border border-black overflow-hidden">
                    <div className="h-full bg-black animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                  <p className="mt-2 text-sm text-black">
                    Processing up to {maxProfilesToSearch} profiles...
                  </p>
                </div>
              ) : aiSearchResults.length > 0 ? (
                <>
                  <div className="bg-white border border-black p-4 mb-4">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 border border-black flex items-center justify-center">
                          <FiZap size={20} className="text-black" />
                        </div>
                        <div>
                          <h3 className="font-medium text-black">
                            AI found {aiSearchResults.length} matching candidates
                            {searchMode === 'job' && selectedJob ? ` for "${selectedJob.title}"` : ''}
                          </h3>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                            <p key="sorted-by" className="text-sm text-black">Sorted by match score</p>
                            {lastSearchTimestamp && (
                              <p key="timestamp" className="text-xs text-black">
                                Search completed {new Date(lastSearchTimestamp).toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="px-3 py-1 border border-black text-black text-sm font-medium flex items-center gap-1">
                          <FiDollarSign size={14} />
                          {creditsUsed} credits used
                        </div>
                        <button 
                          onClick={() => {
                            setSelectedJobId('');
                            setAiSearchResults([]);
                            setAiSearchIntent('');
                            setCreditsUsed(0);
                          }}
                          className="text-black hover:text-gray-700 font-medium"
                        >
                          Clear Results
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Export Options */}
                  <div className="bg-white border border-black p-4 mb-4 flex justify-between items-center">
                    <div className="text-sm text-black">
                      Showing {aiSearchResults.length} matched candidates • You pay {aiSearchResults.length} credits
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="px-3 py-1 border border-black text-black text-sm hover:bg-gray-100 flex items-center gap-1"
                      >
                        <FiDownload size={14} />
                        Export CSV
                      </button>
                    </div>
                  </div>
                  
                  {aiSearchResults.map((candidate) => (
                    <div key={candidate.id} className="bg-white border border-black hover:shadow-md transition-shadow overflow-hidden mb-4">
                      <div className="p-6 flex flex-col md:flex-row gap-6">
                        {/* Candidate Info */}
                        <div className="md:w-2/3">
                          <div className="flex items-center gap-4 mb-4">
                            <div>
                              <h3 className="text-xl font-bold text-black">{candidate.name}</h3>
                              {(candidate.title || candidate.role || candidate.currentCompany) && (
                                <p className="text-black flex items-center gap-2">
                                  <FiBriefcase size={14} className="text-black" />
                                  {candidate.title || candidate.role || (candidate.currentCompany ? `Professional at ${candidate.currentCompany}` : '')}
                                </p>
                              )}
                              <p className="text-black text-sm flex items-center gap-2 mt-1">
                                <FiMapPin size={14} className="text-black" />
                                {candidate.location || 'Location not specified'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-black mb-2">Skills:</p>
                            <div className="flex flex-wrap gap-2">
                              {(() => {
                                // Handle skills - can be array or comma-separated string
                                let skillsList = [];
                                if (Array.isArray(candidate.skills)) {
                                  skillsList = candidate.skills.filter(s => s && s.trim());
                                } else if (typeof candidate.skills === 'string' && candidate.skills.trim()) {
                                  skillsList = candidate.skills.split(',').map(s => s.trim()).filter(s => s);
                                }
                                
                                return skillsList.length > 0 ? (
                                  skillsList.slice(0, 8).map((skill, idx) => (
                                    <span key={`${candidate.id}-skill-${idx}-${skill}`} className="bg-white border border-black text-black text-xs font-medium px-2.5 py-1">
                                      {skill}
                                    </span>
                                  ))
                                ) : (
                                  <span className="bg-gray-100 border border-gray-300 text-gray-500 text-xs font-medium px-2.5 py-1">
                                    No skills listed
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                          
                          {candidate.matchReason && (
                            <div className="mb-4 bg-white p-3 border border-black">
                              <p className="text-sm font-medium text-black mb-1">Match Reason:</p>
                              <p className="text-sm text-black">{candidate.matchReason}</p>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-4">
                            <div key="experience" className="text-sm text-black">
                              <span className="font-medium">Experience:</span> {candidate.experience || 'N/A'} years
                            </div>
                            <div key="status" className="text-sm text-black">
                              <span className="font-medium">Status:</span> {candidate.status || 'Not specified'}
                            </div>
                          </div>
                        </div>
                        
                        {/* AI Match Score */}
                        <div className="md:w-1/3 flex flex-col items-center justify-center border-t md:border-t-0 md:border-l border-black pt-4 md:pt-0 md:pl-6">
                          <div className="flex flex-col items-center">
                            <div className="relative">
                              <svg className="w-32 h-32">
                                <circle 
                                  key="bg-circle"
                                  className="text-gray-200" 
                                  strokeWidth="8" 
                                  stroke="currentColor" 
                                  fill="transparent" 
                                  r="56" 
                                  cx="64" 
                                  cy="64"
                                />
                                <circle 
                                  key="progress-circle"
                                  className="text-black"
                                  strokeWidth="8" 
                                  strokeDasharray={`${candidate.matchScore * 3.51}, 351`} 
                                  strokeLinecap="round" 
                                  stroke="currentColor" 
                                  fill="transparent" 
                                  r="56" 
                                  cx="64" 
                                  cy="64"
                                />
                              </svg>
                              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                                <p key="score-value" className="text-3xl font-bold text-black">
                                  {candidate.matchScore}%
                                </p>
                                <p key="score-label" className="text-xs text-black">Match Score</p>
                              </div>
                            </div>
                            <p className="font-medium text-black mt-2">Match Details</p>
                            <div className="w-full mt-2 space-y-1">
                              {candidate.matchDetails?.map((detail) => (
                                <div key={`${candidate.id}-${detail.type || detail.name}-${detail.match}`} className="flex justify-between items-center">
                                  <span className="text-xs text-black">{detail.type || detail.name}</span>
                                  <div className="flex items-center gap-1">
                                    <div className="w-24 h-2 bg-gray-200 overflow-hidden">
                                      <div 
                                        className="h-full bg-black"
                                        style={{ width: `${detail.match}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-black">{detail.match}%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-white p-3 border-t border-black flex justify-end items-center">
                        <div className="flex gap-2">
                          <button 
                            key="view-profile"
                            onClick={() => navigate(`/candidates/${candidate.id}`, { 
                              state: { 
                                fromAiSearch: activeTab === 'ai',
                                matchScore: candidate.matchScore,
                                matchReason: candidate.matchReason,
                                matchDetails: candidate.matchDetails
                              } 
                            })}
                            className="text-black hover:text-gray-700 text-sm font-medium"
                          >
                            View Profile
                          </button>
                          <button 
                            key="contact"
                            onClick={() => {
                              const inboxPath = user?.userType === 'recruiter' 
                                ? `/recruiter/inbox?candidateId=${candidate.id}&candidateName=${encodeURIComponent(candidate.name)}`
                                : `/inbox?candidateId=${candidate.id}&candidateName=${encodeURIComponent(candidate.name)}`;
                              navigate(inboxPath);
                            }}
                            className="text-black hover:text-gray-700 text-sm font-medium"
                          >
                            Contact
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              ) : searchMode === 'job' && selectedJob ? (
                <div className="bg-white p-8 border border-black text-center">
                  <div className="w-16 h-16 border border-black flex items-center justify-center mx-auto mb-4">
                    <FiSearch size={24} className="text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-2">No matching candidates found</h3>
                  <p className="text-black mb-6">
                    We couldn't find any candidates that match the requirements for "{selectedJob.title}". Try adjusting the job requirements or importing more candidates.
                  </p>
                </div>
              ) : searchMode === 'custom' && aiSearchIntent ? (
                <div className="bg-white dark:bg-dark-800 p-8 rounded-lg shadow-sm text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiSearch size={24} className="text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">No matching candidates found</h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    We couldn't find candidates matching your search criteria. Try adjusting your search terms or importing more candidates.
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-dark-800 p-8 rounded-lg shadow-sm text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiZap size={24} className="text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                    {searchMode === 'job' ? 'Select a Job to Find Matches' : 'Enter Search Criteria'}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    {searchMode === 'job' 
                      ? 'Choose one of your job postings above and our AI will find the best matching candidates based on the job requirements.'
                      : 'Describe the ideal candidate you\'re looking for, and our AI will find the best matches in your candidate database.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Regular Search Results */}
          {activeTab === 'regular' && !loading && !error && (
            searchTerm || location || minExperience || maxExperience || education.length > 0 || statusFilter !== 'All' || workMode || category
          ) && (
            <div className="bg-white p-6 rounded-lg border border-gray-200" style={{ 
              boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-3px) translateY(-3px)'
            }}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Search Results</h2>
              </div>
              
              {filteredCandidates.length > 0 ? (
                <div className="space-y-4">
                  {filteredCandidates.slice(0, 10).map((candidate) => (
                    <div key={candidate.id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div>
                            <h3 className="font-bold text-gray-900">{candidate.name}</h3>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              {(candidate.title || candidate.role || candidate.currentCompany) && (
                                <>
                                  <span key="role">
                                    {candidate.title || candidate.role || (candidate.currentCompany ? `Professional at ${candidate.currentCompany}` : '')}
                                  </span>
                                  <span key="separator">•</span>
                                </>
                              )}
                              <span key="location">{candidate.location || 'Location not specified'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            onClick={() => navigate(`/candidates/${candidate.id}`, { 
                              state: { 
                                fromAiSearch: activeTab === 'ai',
                                matchScore: candidate.matchScore,
                                matchReason: candidate.matchReason,
                                matchDetails: candidate.matchDetails
                              } 
                            })}
                            className="text-yellow-600 hover:text-yellow-800 font-medium text-sm"
                          >
                            View Profile
                          </button>
                          <button 
                            onClick={() => {
                              const inboxPath = user?.userType === 'recruiter' 
                                ? `/recruiter/inbox?candidateId=${candidate.id}&candidateName=${encodeURIComponent(candidate.name)}`
                                : `/inbox?candidateId=${candidate.id}&candidateName=${encodeURIComponent(candidate.name)}`;
                              navigate(inboxPath);
                            }}
                            className="text-yellow-600 hover:text-yellow-800 font-medium text-sm"
                          >
                            Contact
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {filteredCandidates.length > 10 && (
                    <div className="text-center pt-4">
                      <button className="text-yellow-600 hover:text-yellow-800 font-medium">
                        Load More Candidates
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No candidates found matching your criteria.
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DatabasePage;
